import React, { useEffect, useMemo, useRef } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import moment from 'moment/moment';
import { remoteValues } from '~/common/utils/firebase/remoteConfig';
import useAsyncActionStatus from '~/hooks/useAsyncActionStatus';

import { UserCircle } from '~/ui/assets/icons';
import { Form, RenderRichText, RichText, Tooltip } from '~/ui/components';
import CommentTypeBadge from '~/components/CommentTypeBadge/CommentTypeBadge';
import { TicketComment as IComment } from '~/typings/entities/Ticket';
import { getLoggedUser } from '~/store/user/actions';
import { updateTicketComment } from '~/store/tickets/actions';
import {
  BackButton,
  Comment,
  CommentAuthor,
  CommentAuthorRow,
  CommentDate,
  CommentWrap,
  EditButton,
  EditText,
} from './TicketComments.styled';

type TicketCommentProps = {
  comment: IComment;
  ticketId: string;
  onEdit?: (comment: IComment) => void;
};

export function TicketComment({ comment, ticketId, onEdit }: TicketCommentProps) {
  const formRef = useRef(null);
  const [editMode, setEditMode] = React.useState(false);
  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const updateTicketCommentControl = useAsyncActionStatus(updateTicketComment, comment?.id);
  const loggedUser = getLoggedUserControl.result();

  const { commentEditHourLimit } = remoteValues;

  const canEditComment = useMemo(() => {
    // Only internal backoffice notes are editable (legacy comments without a type are backoffice).
    const isBackoffice = !comment.type || comment.type?.name?.toUpperCase() === 'BACKOFFICE';
    const dateRange = moment().diff(moment(comment.createdAt), 'hours');
    return isBackoffice && comment.authorNameId === loggedUser.id && dateRange < commentEditHourLimit;
  }, [comment, loggedUser]);

  const formatDate = (date: string, format = 'DD/MM/YYYY [às] HH:mm') => moment(date).format(format);

  const toggleEditMode = () => setEditMode(!editMode);
  const cancelEditMode = () => setEditMode(false);

  const handleEditComment = ({ description }) => {
    const { id } = comment;
    const newComment = {
      ...comment,
      ticketId,
      description,
    };

    updateTicketCommentControl.start({ ...newComment, id }, { actionId: id });
  };

  useEffect(() => {
    if (updateTicketCommentControl.success) {
      onEdit?.(updateTicketCommentControl.result);
      setEditMode(false);
    }
  }, [updateTicketCommentControl.success]);

  return (
    <Comment key={comment.id}>
      <CommentAuthorRow>
        <UserCircle />
        <CommentAuthor>{comment.authorName}</CommentAuthor>
        {canEditComment && !editMode && <EditButton onClick={toggleEditMode} />}
        {canEditComment && editMode && <BackButton onClick={cancelEditMode} />}
      </CommentAuthorRow>

      {editMode ? (
        <Form
          ref={formRef}
          onSubmit={handleEditComment}
          loading={updateTicketCommentControl.loading ? 1 : 0}
          inputs={[
            {
              id: 'description',
              initialValue: comment.description,
              input: <RichText small showActionButton placeholder="Escreva um novo comentário" controlsVisible />,
              options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
            },
          ]}
        />
      ) : (
        <CommentWrap>
          <CommentDate>
            {formatDate(comment?.createdAt)}{' '}
            {comment?.edited && (
              <Tooltip placement="bottomRight" content={`Última atualização: ${formatDate(comment?.updatedAt)}`}>
                <EditText>(editado)</EditText>
              </Tooltip>
            )}
            <CommentTypeBadge type={comment.type || 'BACKOFFICE'} />
          </CommentDate>

          <RenderRichText html={`${comment.description}`} />
        </CommentWrap>
      )}
    </Comment>
  );
}
